/**
 * מסחר והנחות — the money surface, and the dual control on it. OA-0002, 04 §13.2.1, 08 §8.4 / CF-66.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * WHY THIS IS NOT A PANEL ON THE SUBSCRIPTION SCREEN
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * 08 §8.4 / CF-66: two tenants identical in plan, SKUs, assignments and state and differing ONLY in a
 * discount must produce byte-identical access decisions — *"if changing the price invalidated the
 * entitlement cache, price would be part of entitlement"*. The subscription screen's own header names
 * a capacity view that displays a price as the first place those two get welded back together.
 *
 * So the discount routes were not added there. They are here, on their own address, and the only
 * thing that crosses between the two screens is a link. The separation is the control.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * THREE ROUTES, AND THE THIRD ONE IS THE REASON THE FIRST TWO ARE SAFE
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `platform.discount.apply` applies up to the ordinary ceiling. `platform.discount.apply_full` goes
 * above it, up to and including a full waiver, and is a SEPARATE ROUTE because it is a separate
 * capability held by the platform owner alone — *"the ceiling is enforced by which route the caller
 * can reach"*. This screen therefore offers two distinct actions rather than one action with a
 * percentage box that silently changes meaning at 100.
 *
 * `platform.discount.approve` is the second approver. Without it `PENDING_SECOND_APPROVER` is a state
 * nothing can leave — a discount that was applied, recorded, and permanently unusable. Self-approval
 * is refused by the server against the LIVE approver count, and this screen never predicts that
 * refusal: it sends, and it renders the answer.
 *
 * ── WHAT IS DECLARED AND NEVER DEFAULTED ────────────────────────────────────────────────────────
 *
 * `isRelatedParty`. OA-0002 makes it a FALSIFIABLE ASSERTION attributable to a named requester, and a
 * field that defaults to `false` when omitted is a note rather than an assertion. The control is
 * therefore a required choice with two visible answers, not a checkbox that means "no" when nobody
 * touched it.
 *
 * ── AND THE AMOUNT IS A STRING ON THE WIRE ──────────────────────────────────────────────────────
 *
 * 0003: *money is fixed-point decimal, never binary floating point.* The server reads
 * `discountAmount` as a STRING for that reason, and the field here is a text control that is never
 * parsed into a number on the way out — a `Number()` in this file would defeat the rule at the first
 * `JSON.parse`.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { StateBlock } from '../components/StateBlock';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import type { DiscountBody } from '../api/operations';
import { fixturesEnabled, fixtureTenants } from '../api/fixtures';
import { routes } from '../routes';

export interface PlatformCommercialScreenProps {
  readonly tenantUid: string;
}

const TITLE_HE = 'מסחר, הנחות ואישורים';

/** The three shapes a discount can take. A closed list; there is no fourth and no free text. */
const DISCOUNT_KINDS = ['percentage', 'fixed_amount', 'full_waiver'] as const;
type DiscountKind = (typeof DISCOUNT_KINDS)[number];

const DISCOUNT_KIND_HE: Readonly<Record<DiscountKind, string>> = {
  percentage: 'אחוז הנחה',
  fixed_amount: 'סכום קבוע',
  full_waiver: 'ויתור מלא על החיוב',
};

type Outcome =
  | { readonly kind: 'applied'; readonly needsSecondApprover: boolean }
  | { readonly kind: 'approved' }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

export function PlatformCommercialScreen({ tenantUid }: PlatformCommercialScreenProps): JSX.Element {
  const ctx = useWorkContext();
  const [discountKind, setDiscountKind] = useState<DiscountKind>('percentage');
  const [discountPct, setDiscountPct] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [reason, setReason] = useState('');
  const [relatedParty, setRelatedParty] = useState<'yes' | 'no' | null>(null);
  const [approveUid, setApproveUid] = useState('');
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }

  const tenant = (fixturesEnabled() ? fixtureTenants : []).find((t) => t.tenantUid === tenantUid) ?? null;
  const back = { labelHe: 'הלקוחות', href: routes.platformTenants };

  if (tenant === null) {
    return (
      <AppShell titleHe={TITLE_HE} backToParent={back}>
        <StateBlock
          titleHe="הלקוח אינו זמין"
          bodyHe="לא נמצא הלקוח שאליו מפנה הקישור."
          tone="unavailable"
          nextStep={
            <a className="btn btn--primary" href={routes.platformTenants}>
              חזרה לרשימת הלקוחות
            </a>
          }
        />
      </AppShell>
    );
  }

  async function applyDiscount(aboveCeiling: boolean): Promise<void> {
    setSending(true);
    setOutcome(null);
    try {
      // ONE BODY, TWO OPERATIONS — and the two calls are written out rather than selected by a
      // string. `apply_full` is a SEPARATE ACTION reserved to the platform owner and requiring a
      // second approver, not the same action with a larger number, and a path assembled as
      // `discounts${aboveCeiling ? '/full' : ''}` said that in a way no reader and no scanner could
      // see. Spelled out, each action is individually visible to the reachability scan: deleting
      // either control fails CI on its own rather than hiding behind the other.
      const body: DiscountBody = {
        discountKind,
        // A percentage is a number; an AMOUNT is a string and stays one. 0003.
        discountPct: discountPct === '' ? null : Number(discountPct),
        discountAmount: discountAmount === '' ? null : discountAmount,
        currencyCode: null,
        scope: 'subscription',
        scopeSkuCode: null,
        effectiveFrom,
        effectiveTo: null,
        reason,
        // DECLARED, never defaulted. OA-0002.
        isRelatedParty: relatedParty === 'yes',
      };
      const params = { tenant_uid: tenantUid };
      const result = aboveCeiling
        ? await callOperation('platform.discount.apply_full', { params, body })
        : await callOperation('platform.discount.apply', { params, body });
      setOutcome({
        kind: 'applied',
        needsSecondApprover: result?.outcome === 'PENDING_SECOND_APPROVER',
      });
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setSending(false);
    }
  }

  async function approve(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSending(true);
    setOutcome(null);
    try {
      await callOperation('platform.discount.approve', {
        params: { discount_uid: approveUid },
        body: {},
      });
      setOutcome({ kind: 'approved' });
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setSending(false);
    }
  }

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'כרטיס הלקוח', href: routes.platformTenant(tenant.tenantUid) }}
      provenanceHe="החלטות מסחריות נרשמות בנפרד מהזכאות. הנחה אינה משנה מה הלקוח רשאי לעשות, אלא רק מה הוא מחויב עליו."
    >
      <section className="notice" data-tone="info">
        <h2 className="notice__title">{`החלטות מסחריות עבור ${tenant.legalName}`}</h2>
        <p className="notice__body">
          הנחה אינה משפיעה על ההרשאות ועל המכסות של הלקוח. שני לקוחות זהים שנבדלים בהנחה בלבד מקבלים
          בדיוק אותן החלטות גישה, ולכן ההנחות נמצאות במסך נפרד ממסך המכסות.
        </p>
        <p className="notice__body">
          הנחה מעל התקרה הרגילה היא פעולה נפרדת, ולא אותה פעולה עם מספר גדול יותר. היא שמורה לבעלים
          של הפלטפורמה ודורשת אישור של אדם שני.
        </p>
        <div className="actions-row">
          <a className="btn" href={routes.platformTenantSubscription(tenant.tenantUid)}>
            מנוי ומכסות
          </a>
        </div>
      </section>

      {outcome === null ? null : outcome.kind === 'applied' ? (
        <StateBlock
          titleHe={outcome.needsSecondApprover ? 'ההנחה נרשמה וממתינה לאישור שני' : 'ההנחה נרשמה'}
          bodyHe={
            outcome.needsSecondApprover
              ? 'ההחלטה נרשמה ואינה בתוקף עד שאדם שני יאשר אותה. אישור עצמי אינו אפשרי.'
              : 'ההחלטה נרשמה ביומן יחד עם הסיבה, עם ההצהרה על צד קשור ועם מי שביצע אותה.'
          }
          nextStep={
            outcome.needsSecondApprover ? (
              <p>המאשר השני מזין את מזהה ההחלטה בטופס האישור שבמסך הזה.</p>
            ) : undefined
          }
        />
      ) : outcome.kind === 'approved' ? (
        <StateBlock
          titleHe="ההחלטה אושרה"
          bodyHe="האישור נרשם יחד עם המאשר. ההחלטה יצאה ממצב ההמתנה."
        />
      ) : (
        <ActionFailureNotice failure={outcome.failure} />
      )}

      <form
        className="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void applyDiscount(false);
        }}
      >
        <h2 className="form__title">מתן הנחה</h2>

        <fieldset className="field field--choice">
          <legend className="field__label">סוג ההנחה</legend>
          {DISCOUNT_KINDS.map((kind) => (
            <label key={kind} className="choice">
              <input
                type="radio"
                name="discountKind"
                value={kind}
                checked={discountKind === kind}
                onChange={() => setDiscountKind(kind)}
              />
              <span>{DISCOUNT_KIND_HE[kind]}</span>
            </label>
          ))}
        </fieldset>

        <label className="field">
          <span className="field__label">אחוז ההנחה</span>
          <input
            className="field__input"
            name="discountPct"
            dir="ltr"
            inputMode="numeric"
            value={discountPct}
            disabled={discountKind !== 'percentage'}
            onChange={(e) => setDiscountPct(e.currentTarget.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">סכום ההנחה</span>
          <input
            className="field__input"
            name="discountAmount"
            dir="ltr"
            value={discountAmount}
            disabled={discountKind !== 'fixed_amount'}
            onChange={(e) => setDiscountAmount(e.currentTarget.value)}
          />
          <span className="field__hint">
            הסכום נשמר כפי שהוזן, ספרה בספרה. הוא אינו מומר למספר עשרוני בדרך לשרת.
          </span>
        </label>

        <label className="field">
          <span className="field__label">מועד תחילת ההנחה</span>
          <input
            className="field__input"
            name="effectiveFrom"
            dir="ltr"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.currentTarget.value)}
          />
        </label>

        {/*
          A REQUIRED CHOICE WITH TWO VISIBLE ANSWERS, not a checkbox. OA-0002 makes this a falsifiable
          assertion by a named requester; a control that means "no" when nobody touched it turns the
          assertion back into a note.
        */}
        <fieldset className="field field--choice">
          <legend className="field__label">האם הלקוח הוא צד קשור</legend>
          <label className="choice">
            <input
              type="radio"
              name="isRelatedParty"
              value="no"
              checked={relatedParty === 'no'}
              onChange={() => setRelatedParty('no')}
            />
            <span>אינו צד קשור</span>
          </label>
          <label className="choice">
            <input
              type="radio"
              name="isRelatedParty"
              value="yes"
              checked={relatedParty === 'yes'}
              onChange={() => setRelatedParty('yes')}
            />
            <span>צד קשור</span>
          </label>
          <span className="field__hint">
            ההצהרה נרשמת על שמך. היא ניתנת לבדיקה בדיעבד, ולכן אין לה ערך ברירת מחדל.
          </span>
        </fieldset>

        <label className="field">
          <span className="field__label">סיבת ההנחה</span>
          <input
            className="field__input"
            name="discount-reason"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
          />
        </label>

        <div className="actions-row">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={sending || reason === '' || relatedParty === null || effectiveFrom === ''}
          >
            מתן הנחה בתוך התקרה
          </button>
          <button
            type="button"
            className="btn btn--irreversible"
            disabled={sending || reason === '' || relatedParty === null || effectiveFrom === ''}
            onClick={() => void applyDiscount(true)}
          >
            מתן הנחה מעל התקרה
          </button>
        </div>
        <p className="form__note">
          שתי הפעולות נפרדות בכוונה. מי שאינו רשאי לחרוג מהתקרה יקבל סירוב על הפעולה השנייה, ולא
          יגלה זאת רק אחרי שהזין מספר גדול.
        </p>
      </form>

      <form className="form" onSubmit={(e) => void approve(e)}>
        <h2 className="form__title">אישור החלטה שממתינה למאשר שני</h2>
        <p className="form__lead">
          בלי האישור הזה החלטה שממתינה נשארת תקועה — נרשמה, ואינה בתוקף. אישור עצמי נדחה בשרת מול
          ספירת המאשרים בפועל.
        </p>
        <label className="field">
          <span className="field__label">מזהה ההחלטה</span>
          <input
            className="field__input"
            name="discountUid"
            dir="ltr"
            value={approveUid}
            onChange={(e) => setApproveUid(e.currentTarget.value)}
          />
        </label>
        <div className="actions-row">
          <button type="submit" className="btn btn--primary" disabled={sending || approveUid === ''}>
            אישור ההחלטה
          </button>
        </div>
      </form>
    </AppShell>
  );
}
